import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapPin, Calendar, Images } from 'lucide-react';
import { RETREAT_YEARS } from './retreatsData';

const YearGalleryDialog = ({ year, onClose }) => (
  <Dialog open={!!year} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="year-gallery-dialog">
      {year && (
        <>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-forest">
              {year.year} — {year.theme}
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">{year.description}</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            {year.events.map((e) => (
              <div key={`${e.title}-${e.date}`} className="bg-cream rounded-xl overflow-hidden border border-sand">
                {e.image && (
                  <img src={e.image} alt={`${e.title} ${e.location}`} className="w-full h-44 object-cover" loading="lazy" />
                )}
                <div className="p-4">
                  <p className="font-heading text-forest font-bold">{e.title}</p>
                  <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600">
                    {e.date && (
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-terracotta" />{e.date}</span>
                    )}
                    {e.location && (
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-terracotta" />{e.location}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const RetreatJourneyByYear = () => {
  const [openYear, setOpenYear] = useState(null);

  return (
    <section id="journey" className="py-20 px-6 bg-white" data-testid="retreat-journey-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <Badge className="bg-terracotta/10 text-terracotta mb-4">Our Retreat Journey</Badge>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-3">Years of Choosing Ourselves</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            From our first exhale in Aruba to soft power in Costa Rica — every gathering has been a chapter in the same story.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {RETREAT_YEARS.map((y) => (
            <div
              key={y.year}
              className="group relative rounded-2xl overflow-hidden shadow-lg border border-sand bg-forest min-h-[380px] flex flex-col justify-end"
              data-testid={`journey-year-${y.year}`}
            >
              {y.coverImage ? (
                <img
                  src={y.coverImage}
                  alt={`${y.year} ${y.theme}`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-forest via-forest/90 to-terracotta/50" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/60 to-forest/10" />
              <div className="relative p-6 text-white">
                <p className="font-heading text-5xl font-bold text-terracotta drop-shadow">{y.year}</p>
                <h3 className="font-heading text-2xl font-bold mt-1">{y.theme}</h3>
                <p className="text-white/85 text-sm mt-2 leading-relaxed">{y.description}</p>
                <Button
                  onClick={() => setOpenYear(y)}
                  size="sm"
                  className="mt-4 bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm border border-white/30"
                  data-testid={`view-experience-${y.year}-btn`}
                >
                  <Images className="h-4 w-4 mr-2" /> View the Experience
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <YearGalleryDialog year={openYear} onClose={() => setOpenYear(null)} />
    </section>
  );
};
